import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  createFileRoute,
  useNavigate,
} from '@tanstack/react-router'
import { useSetAtom } from 'jotai'
import { nanoid } from 'nanoid'
import type { UIMessage } from 'ai'
import { Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { apiEndpoints } from '@/lib/env'
import { deserializeFromMarkdown } from '@/tiptap'
import {
  type ProposedRunbookDraft,
  extractRunbookDraftFromToolPart,
} from '@/components/chat/components/messages/tools/RunbookDraftToolMessages'
import { extractRunbookDraftFromDocument } from '@/components/compliance/runbook-draft'
import {
  buildRunbookParamMapFromDrafts,
  createRunbookParamDraft,
  type RunbookParamDraft,
} from '@/components/compliance/RunbookParamEditor'
import {
  ScreenataPageLayout,
  type ScreenataChatConfig,
} from '@/components/compliance/ScreenataPageLayout'
import { ChatThreadManager } from '@/components/chat/ChatThreadManager'
import {
  currentThreadIdAtom,
  updateThreadNameAtom,
} from '@/components/chat/store/chatThreadStore'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { buildableRunbookPlanAtom } from '@/stores/runbookDraftStore'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/runbooks_/new'
)({
  validateSearch: (search: Record<string, unknown>) => {
    const rawPrompt = search.initialPrompt
    const rawTemplate = search.templateId
    return {
      initialPrompt:
        typeof rawPrompt === 'string' && rawPrompt.trim().length > 0
          ? rawPrompt
          : undefined,
      templateId:
        typeof rawTemplate === 'string' && rawTemplate.trim().length > 0
          ? rawTemplate
          : undefined,
    }
  },
  component: NewRunbookDraftPage,
})

const RUNBOOK_REF_RE =
  /@(?:step|params|run|env)\.[A-Za-z0-9_][A-Za-z0-9_.[\]-]*/g

type JsonNode = {
  type?: string
  text?: string
  attrs?: Record<string, unknown>
  content?: JsonNode[]
} & Record<string, unknown>

/**
 * The runbook editor's TipTap schema is intentionally narrow:
 *   Document.content = 'orderedList'    (every top-level child is a step)
 *   StarterKit disables horizontalRule, blockquote, heading, codeBlock, strike.
 * Any node that isn't in the schema causes `Schema.fromJSON` to throw
 * `Unknown node type: …`. Vera's markdown frequently includes `---`
 * separators between sections (deserialised as `horizontalRule`) and
 * `>` blockquotes for callouts, neither of which the editor accepts.
 * Strip those nodes recursively before handing the doc to the editor —
 * fail-soft behaviour is better than dropping the entire draft.
 */
const UNSUPPORTED_NODE_TYPES = new Set([
  'horizontalRule',
  'horizontal_rule',
  'blockquote',
  'codeBlock',
  'code_block',
  // RFC-167 §13.5 — `<StepMeta role=… dueDays=… />` is a server-only TEXT
  // marker, not a registered editor node. deserializeFromMarkdown turns it into
  // an unknown inline node that crashes the editor's Schema.fromJSON on display
  // (and makes renderToMarkdown throw). Strip it from the editor doc; the
  // role/due it carries is preserved in the persisted markdown (which is the
  // server-authored `draft.markdown`, see below) and re-extracted server-side
  // by extractRunbookSteps.
  'StepMeta',
])

function stripUnsupportedNodes(node: unknown): unknown {
  if (!node || typeof node !== 'object') return node
  if (Array.isArray(node)) {
    return node
      .map((child) => stripUnsupportedNodes(child))
      .filter((child) => child !== undefined)
  }
  const n = node as JsonNode
  if (typeof n.type === 'string' && UNSUPPORTED_NODE_TYPES.has(n.type)) {
    return undefined
  }
  if (Array.isArray(n.content)) {
    return {
      ...n,
      content: stripUnsupportedNodes(n.content) as JsonNode[],
    }
  }
  return n
}

function liftRunbookRefMentions(node: unknown): unknown {
  if (!node || typeof node !== 'object') return node
  if (Array.isArray(node)) {
    return node.flatMap((child) => {
      const lifted = liftRunbookRefMentions(child)
      return Array.isArray(lifted) ? lifted : [lifted]
    })
  }
  const n = node as JsonNode
  if (n.type === 'text' && typeof n.text === 'string') {
    const split = splitTextWithRefs(n)
    if (split) return split
    return n
  }
  if (Array.isArray(n.content)) {
    return {
      ...n,
      content: (liftRunbookRefMentions(n.content) as JsonNode[]) ?? n.content,
    }
  }
  return n
}

function splitTextWithRefs(textNode: JsonNode): JsonNode[] | null {
  const text = String(textNode.text ?? '')
  RUNBOOK_REF_RE.lastIndex = 0
  if (!RUNBOOK_REF_RE.test(text)) return null
  RUNBOOK_REF_RE.lastIndex = 0
  const parts: JsonNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  while ((match = RUNBOOK_REF_RE.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push({
        type: 'text',
        text: text.slice(cursor, match.index),
        ...(textNode.marks ? { marks: textNode.marks } : {}),
      })
    }
    parts.push({ type: 'runbookRefMention', attrs: { ref: match[0] } })
    cursor = match.index + match[0].length
  }
  if (cursor < text.length) {
    parts.push({
      type: 'text',
      text: text.slice(cursor),
      ...(textNode.marks ? { marks: textNode.marks } : {}),
    })
  }
  return parts
}

function mapProposedParamType(type: string): RunbookParamDraft['type'] {
  switch (type) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'url':
    case 'email':
    case 'date':
    case 'enum':
      return type
    default:
      return 'string'
  }
}

/**
 * Runbook drafting station. Mounted at /runbooks/new.
 *
 * Layout: ScreenataPageLayout chatMode='full' — the embedded
 * ChatPanel takes the entire content area while the runbook is
 * being drafted. No coordination with the workspace shell.
 *
 * Flow:
 *   1. Modal hands us `initialPrompt` (and optional `templateId`).
 *   2. We mint a fresh chat thread on mount via ChatThreadManager so
 *      the chat panel adopts a stable threadId.
 *   3. ScreenataPageLayout renders ChatPanel with the runbook skill
 *      auto-loaded, the user's prompt queued as the first visible message,
 *      and starter-template context carried in the request body.
 *   4. Vera fetches policy and emits `proposeRunbook` — an ephemeral PLAN.
 *      Nothing is persisted; the in-chat plan card renders the steps and the
 *      user refines them in chat (Vera re-emits `proposeRunbook` per change).
 *   5. The user clicks "Build runbook" on the latest plan card → we persist
 *      via `runbook.createRunbook` and redirect to `/runbooks/$id`. No draft
 *      is materialised until the user explicitly builds.
 */
function NewRunbookDraftPage() {
  const { workspaceId } = Route.useParams()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const { initialPrompt: rawInitialPrompt, templateId } = Route.useSearch()
  const initialPrompt = useMemo(() => {
    if (!rawInitialPrompt) return undefined
    return rawInitialPrompt
  }, [rawInitialPrompt])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const setCurrentThreadId = useSetAtom(currentThreadIdAtom)
  const setPageContext = useSetAtom(screenataPageContextAtom)
  const setBuildablePlan = useSetAtom(buildableRunbookPlanAtom)
  const renameThread = useSetAtom(updateThreadNameAtom)

  const programQuery = useQuery({
    ...trpc.complianceProgram.getProgram.queryOptions(),
  })
  const programId = programQuery.data?.id as string | undefined

  const createMutation = useMutation(
    trpc.runbook.createRunbook.mutationOptions(),
  )
  const { mutateAsync: createRunbook } = createMutation

  const threadIdRef = useRef<string>(`runbook-draft-${nanoid()}`)
  const threadId = threadIdRef.current
  const [threadReady, setThreadReady] = useState(false)
  const lastSeenToolCallRef = useRef<string | null>(null)
  const persistInFlightRef = useRef(false)
  // Latest plan Vera has emitted, awaiting the user's explicit "Build procedure".
  // Held in a ref so the build-event listener reads it without re-binding.
  const pendingDraftRef = useRef<ProposedRunbookDraft | null>(null)
  // Last plan title we used to name the thread — avoids redundant renames.
  const namedTitleRef = useRef<string | null>(null)

  useEffect(() => {
    if (!canWorkspaceWrite) {
      setThreadReady(false)
      return
    }
    let cancelled = false
    const ensureThread = async () => {
      const existing = await ChatThreadManager.getThread(threadId)
      if (!existing) {
        await ChatThreadManager.createThread(
          'New procedure',
          undefined,
          undefined,
          threadId,
          initialPrompt ? { documents: [] } : undefined,
        )
      }
      if (cancelled) return
      setCurrentThreadId(threadId)
      setThreadReady(true)
    }
    void ensureThread()
    return () => {
      cancelled = true
      // Drop the draft thread from the global currentThreadIdAtom on unmount.
      // Otherwise downstream pages (e.g. the runbook detail page's Ask AI
      // button → workspace chat) inherit the stale draft thread id and fail
      // to open a fresh conversation.
      setCurrentThreadId(null)
    }
  }, [canWorkspaceWrite, initialPrompt, setCurrentThreadId, threadId])

  useEffect(() => {
    setPageContext({
      page: 'runbook-draft',
      summary: 'Drafting a new runbook',
    })
    return () => setPageContext(null)
  }, [setPageContext])

  const handleProposeDraft = useCallback(
    async (draft: ProposedRunbookDraft) => {
      if (!canWorkspaceWrite) return
      if (persistInFlightRef.current) return
      persistInFlightRef.current = true
      setBuildablePlan((prev) => ({ ...prev, building: true }))

      // Release the build lock + flag so the plan card re-enables its button.
      const abortBuild = () => {
        persistInFlightRef.current = false
        setBuildablePlan((prev) => ({ ...prev, building: false }))
      }

      let parsed: import('@tiptap/core').JSONContent | null = null
      try {
        const doc = deserializeFromMarkdown(draft.markdown).content
        const stripped = stripUnsupportedNodes(doc)
        parsed = liftRunbookRefMentions(stripped) as import('@tiptap/core').JSONContent
      } catch (err) {
        console.error('[NewRunbookDraft] markdown parse failed', err)
        toast.error('Could not render the generated procedure. Try again.')
        abortBuild()
        return
      }

      const derived = extractRunbookDraftFromDocument({ documentBody: parsed })
      const paramDrafts: RunbookParamDraft[] = (draft.params ?? []).map(
        (proposed) =>
          createRunbookParamDraft({
            name: proposed.name,
            description: proposed.description ?? '',
            type: mapProposedParamType(proposed.type),
            required: proposed.required,
            value: proposed.defaultValue ?? '',
            enumOptions: proposed.enumOptions.join(', '),
            validation: proposed.validation ?? undefined,
          }),
      )
      const paramBuild = buildRunbookParamMapFromDrafts(paramDrafts)
      if (paramBuild.error) {
        toast.error(paramBuild.error)
        abortBuild()
        return
      }

      try {
        const result = await createRunbook({
          title: draft.title,
          description: draft.description ?? null,
          documentBody: derived.documentBody,
          // Persist Vera's own `draft.markdown`, NOT the TipTap-round-tripped
          // `derived.instructionsMarkdown`. The round-trip can't re-serialize
          // server-only inline markers (e.g. <StepMeta/>, RFC-167 §13.5) and
          // collapses to empty markdown, which would silently persist a runbook
          // with ZERO steps (and drop every branch edge). `draft.markdown` is
          // position-aligned with the `step-N` ids the branch dependencies
          // reference and is parsed natively by the server's extractRunbookSteps.
          instructionsMarkdown: draft.markdown,
          runbookParams: paramBuild.params,
          // Structured steps: persist verbatim via saveDefinition's structured
          // channel so each human step's explicit form survives (the markdown
          // above is kept only for preview / the document view). Only sent when
          // non-empty — an empty array would still select the structured channel
          // and persist a zero-step runbook.
          ...(draft.steps.length > 0
            ? {
                steps: draft.steps,
                viewPreference: 'graph' as const,
              }
            : {}),
          // RFC-167 §13.6 — a branched procedure can't be expressed in the
          // linear markdown body, so carry the edges through to
          // `Workflow.dependencies` and default the saved view to the graph.
          // The runbook detail route honors `viewPreference` and renders the
          // branch forks via RunbookDefinitionGraph.
          ...(draft.dependencies.length > 0
            ? {
                dependencies: draft.dependencies,
                viewPreference: 'graph' as const,
              }
            : {}),
          ...(templateId
            ? {
                sourceTemplateId: templateId,
                templateCustomization: rawInitialPrompt,
              }
            : {}),
          ...(draft.proposalHash
            ? { proposalHash: draft.proposalHash }
            : {}),
        })
        await queryClient.invalidateQueries({
          queryKey: trpc.runbook.listRunbooks.queryKey(),
        })
        toast.success('Procedure drafted')
        navigate({
          to: '/workspaces/$workspaceId/runbooks/$runbookId',
          params: { workspaceId, runbookId: result.id },
          replace: true,
        })
      } catch (err) {
        // The server validates the structure on persist and rolls back the row
        // on failure, so nothing partial is saved. Surface the specific reason
        // and keep the staged plan on screen (abortBuild only releases the lock)
        // so the user can ask Vera to fix it and rebuild — no work is lost.
        console.error('[NewRunbookDraft] createRunbook failed', err)
        const reason = err instanceof Error ? err.message.trim() : ''
        toast.error(
          reason
            ? `Couldn't build the procedure: ${reason}. Ask Vera to fix it, then rebuild.`
            : "Couldn't build the procedure. Ask Vera to adjust the plan, then rebuild.",
          { duration: 8000 },
        )
        abortBuild()
      }
    },
    [
      createRunbook,
      canWorkspaceWrite,
      navigate,
      queryClient,
      rawInitialPrompt,
      setBuildablePlan,
      templateId,
      workspaceId,
    ]
  )

  // Watch the stream for the latest `proposeRunbook` plan and stage it for an
  // explicit build. We do NOT persist here — `proposeRunbook` is an ephemeral
  // plan the user reviews and refines in chat; persistence happens only when
  // they click "Build procedure" (→ `screenata:build-runbook`, handled below).
  const handleMessagesChange = useCallback(
    (messages: UIMessage[]) => {
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const message = messages[i] as unknown as { id: string; parts?: any[] }
        for (const part of message.parts ?? []) {
          const isPropose =
            part.type === 'tool-proposeRunbook' ||
            (part.type === 'tool-result' && part.toolName === 'proposeRunbook')
          if (!isPropose) continue
          const callId = part.toolCallId ?? `${message.id}:proposeRunbook`
          if (lastSeenToolCallRef.current === callId) continue
          const draft = extractRunbookDraftFromToolPart(part)
          if (!draft) {
            console.debug('[NewRunbookDraft] proposeRunbook part seen but no draft extracted', {
              callId,
              partState: part.state,
              hasOutput: Boolean(part.output ?? part.result),
            })
            continue
          }
          console.info('[NewRunbookDraft] staging proposeRunbook plan', {
            callId,
            title: draft.title,
            steps: draft.steps.length,
          })
          lastSeenToolCallRef.current = callId
          pendingDraftRef.current = draft
          // Flag that a buildable plan exists so the chat plan card shows its
          // "Build procedure" button (clicking builds this latest staged plan).
          setBuildablePlan({ callId, building: false })
          // Name the thread after the procedure so the sidebar isn't a wall of
          // "New procedure" rows. Only on a title change (refinement turns reuse
          // the same title).
          const title = draft.title.trim()
          if (title && namedTitleRef.current !== title) {
            namedTitleRef.current = title
            void renameThread({
              threadId,
              name: `Draft procedure for ${title}`,
            })
          }
          return
        }
      }
    },
    [setBuildablePlan, renameThread, threadId],
  )

  // The plan card's "Build runbook" button dispatches this; we persist the
  // staged plan and redirect. Lives here (not on the card) because the page
  // owns the `createRunbook` mutation + navigation.
  useEffect(() => {
    const handler = () => {
      if (!canWorkspaceWrite) return
      const draft = pendingDraftRef.current
      if (!draft) return
      void handleProposeDraft(draft)
    }
    window.addEventListener('screenata:build-runbook', handler)
    return () =>
      window.removeEventListener('screenata:build-runbook', handler)
  }, [canWorkspaceWrite, handleProposeDraft])

  // Clear the cross-component buildable flag when leaving the drafting page so
  // a stale plan card elsewhere can't offer a build.
  useEffect(() => {
    return () => setBuildablePlan({ callId: null, building: false })
  }, [setBuildablePlan])

  const chatRequestBody = useMemo(
    () => ({
      ...(programId ? { programId } : {}),
      turnContext: {
        ...(programId ? { programId } : {}),
        autoLoadSkills: [
          {
            name: 'runbook-authoring',
            ...(templateId
              ? {
                  activationParams: {
                    templateId,
                    ...(rawInitialPrompt
                      ? { templateCustomization: rawInitialPrompt }
                      : {}),
                  },
                }
              : {}),
          },
        ],
        pageContext: {
          page: 'runbook-draft',
          summary: 'Drafting a new runbook',
          ...(templateId
            ? {
                data: {
                  runbookAuthoring: {
                    templateId,
                    ...(rawInitialPrompt
                      ? { templateCustomization: rawInitialPrompt }
                      : {}),
                  },
                },
              }
            : {}),
        },
      },
    }),
    [programId, rawInitialPrompt, templateId],
  )

  const chatConfig = useMemo<ScreenataChatConfig | undefined>(() => {
    if (!canWorkspaceWrite) return undefined
    if (!threadReady) return undefined
    return {
      api: apiEndpoints.screenataChat,
      requestBody: chatRequestBody,
      autoStartMessage: initialPrompt
        ? { message: initialPrompt, body: chatRequestBody }
        : undefined,
      onMessagesChange: handleMessagesChange,
      sendOnlyLastMessage: false,
    }
  }, [canWorkspaceWrite, chatRequestBody, handleMessagesChange, initialPrompt, threadReady])

  if (!canWorkspaceWrite) {
    return (
      <ScreenataPageLayout title='New procedure' raw chatMode='none'>
        <div className='flex h-[calc(var(--app-vh)_-_50px)] w-full items-center justify-center bg-card px-6 text-center'>
          <div className='max-w-md space-y-2'>
            <p className='text-[14px] font-medium text-text-secondary'>
              Procedure drafting is read-only for your role.
            </p>
            <p className='text-[13px] text-text-subtle'>
              Ask a workspace contributor or admin to create or edit procedures.
            </p>
          </div>
        </div>
      </ScreenataPageLayout>
    )
  }

  return (
    <ScreenataPageLayout
      title='New procedure'
      raw
      chatMode={chatConfig ? 'full' : 'none'}
      chatConfig={chatConfig}
    >
      <div className='flex h-[calc(var(--app-vh)_-_50px)] w-full items-center justify-center bg-card'>
        <div className='flex flex-col items-center gap-3 text-center'>
          <div className='inline-flex items-center gap-2 rounded-sm border border-ring/30 bg-selected px-3 py-1.5'>
            <Sparkles className='h-3.5 w-3.5 animate-pulse text-cobalt-strong' />
            <span className='text-[11px] font-medium tracking-wide text-cobalt-strong uppercase'>
              {createMutation.isPending ? 'Building runbook' : 'Vera is planning'}
            </span>
          </div>
          <p className='max-w-md text-[13px] text-text-subtle'>
            {createMutation.isPending
              ? 'Building the runbook and opening it…'
              : 'Setting up your drafting session.'}
          </p>
          <Loader2 className='h-5 w-5 animate-spin text-status-blue' />
        </div>
      </div>
    </ScreenataPageLayout>
  )
}
